import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { attachmentsApi } from '@/api/attachmentsApi'
import { messagesApi } from '@/api/messagesApi'
import { ticketsApi, type CreateTicketInput, type CreatedTicket, type TicketDetail } from '@/api/ticketsApi'
import { errorMessage } from '@/components/common/States'
import { TICKET_COUNTS_KEY } from '@/hooks/useTicketCounts'
import { pendingUploads, type PendingUpload } from '@/hooks/usePendingUploads'
import type { FollowUpStep } from '@/lib/manualTicket'

export type StepKey = 'create' | FollowUpStep
export type StepState = 'pending' | 'running' | 'done' | 'failed'
export interface StepProgress {
  key: StepKey
  state: StepState
  detail?: string
}

export interface ManualTicketSubmission {
  payload: CreateTicketInput
  steps: FollowUpStep[]
  files: { file: File; visibility: 'customer' | 'internal' }[]
  note?: { body: string; mentions: string[] }
}

export interface ManualTicketResult {
  ticket: TicketDetail | null
  /** Steps other than attachments that failed (the ticket exists). */
  failed: FollowUpStep[]
  /** Files that failed; also parked in `pendingUploads` for the ticket page's retry. */
  failedUploads: PendingUpload[]
  createError: string | null
}

/**
 * Runs the full-page form's submission in order: create (with the intake
 * context, GxP note and a new contact's role) → attachments (presign → PUT →
 * confirm each, on the description message `firstMessageId`) → internal note.
 * Everything after create is best-effort: a failure is reported per step and
 * never undoes the ticket.
 */
export function useSubmitManualTicket() {
  const queryClient = useQueryClient()
  const [progress, setProgress] = useState<StepProgress[]>([])
  const [running, setRunning] = useState(false)

  const submit = useCallback(
    async (s: ManualTicketSubmission): Promise<ManualTicketResult> => {
      const keys: StepKey[] = ['create', ...s.steps]
      let current: StepProgress[] = keys.map((key) => ({ key, state: 'pending' }))
      const mark = (key: StepKey, state: StepState, detail?: string) => {
        current = current.map((p) => (p.key === key ? { ...p, state, detail } : p))
        setProgress(current)
      }
      setProgress(current)
      setRunning(true)
      const result: ManualTicketResult = { ticket: null, failed: [], failedUploads: [], createError: null }
      try {
        mark('create', 'running')
        let ticket: CreatedTicket
        try {
          ticket = await ticketsApi.create(s.payload)
        } catch (err) {
          result.createError = errorMessage(err)
          mark('create', 'failed', result.createError)
          return result
        }
        result.ticket = ticket
        mark('create', 'done', ticket.key)

        for (const step of s.steps) {
          mark(step, 'running')
          try {
            if (step === 'attachments') {
              for (const { file, visibility } of s.files) {
                try {
                  const meta = { fileName: file.name, mimeType: file.type || 'application/octet-stream', sizeBytes: file.size }
                  const presign = await attachmentsApi.presign(ticket.id, meta)
                  await attachmentsApi.uploadBytes(presign, file)
                  // Customer-visible files go on the description message, like an
                  // email's attachments. An attachment on a message takes the
                  // message's visibility, so internal-only files stay off it.
                  await attachmentsApi.confirm(ticket.id, {
                    storageKey: presign.storageKey,
                    ...meta,
                    ...(visibility === 'customer' ? { messageId: ticket.firstMessageId } : {}),
                    visibility,
                  })
                } catch (err) {
                  result.failedUploads.push({ file, visibility, error: errorMessage(err) })
                }
              }
              if (result.failedUploads.length) {
                pendingUploads.set(ticket.id, result.failedUploads)
                throw new Error(result.failedUploads.map((f) => f.file.name).join(', '))
              }
            } else if (step === 'note' && s.note) {
              await messagesApi.post(ticket.id, {
                kind: 'internal_note',
                body: s.note.body,
                mentions: s.note.mentions.length ? s.note.mentions : undefined,
              })
            }
            mark(step, 'done')
          } catch (err) {
            if (step !== 'attachments') result.failed.push(step)
            mark(step, 'failed', errorMessage(err))
          }
        }
        return result
      } finally {
        setRunning(false)
        void queryClient.invalidateQueries({ queryKey: ['tickets'] })
        void queryClient.invalidateQueries({ queryKey: TICKET_COUNTS_KEY })
        if (result.ticket) {
          void queryClient.invalidateQueries({ queryKey: ['ticket', result.ticket.key] })
          void queryClient.invalidateQueries({ queryKey: ['contacts', result.ticket.customer.id] })
        }
      }
    },
    [queryClient],
  )

  return { submit, progress, running, reset: () => setProgress([]) }
}
